const BASE_URL = import.meta.env.VITE_API_URL || '/api';

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

// ─── Refresh Token Mutex ──────────────────────────────────────
// Prevents concurrent 401 handlers from racing on token rotation.
// All callers share the same in-flight refresh promise.
let refreshPromise: Promise<boolean> | null = null;

export async function tryRefreshToken(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const res = await fetch(`${BASE_URL}/auth/refresh`, { method: 'POST', credentials: 'include' });
      if (!res.ok) return false;
      const data = await res.json() as { accessToken: string };
      localStorage.setItem('access_token', data.accessToken);
      return true;
    } catch {
      return false;
    }
  })();
  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

// ─── Request Helper ───────────────────────────────────────────

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const token = localStorage.getItem('access_token');
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options?.headers,
  };

  const res = await fetch(`${BASE_URL}${path}`, { ...options, headers, credentials: 'include' });

  if (res.status === 401) {
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      const newToken = localStorage.getItem('access_token');
      const retryRes = await fetch(`${BASE_URL}${path}`, {
        ...options,
        headers: { ...headers, Authorization: `Bearer ${newToken}` },
        credentials: 'include',
      });
      if (retryRes.ok) {
        if (retryRes.status === 204 || retryRes.headers.get('content-length') === '0') return undefined as T;
        return retryRes.json() as Promise<T>;
      }
    }
    // Clear token — React Router handles redirect via auth store reactivity
    localStorage.removeItem('access_token');
    throw new ApiError(401, 'Session expired');
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText })) as { error?: string };
    throw new ApiError(res.status, body.error ?? res.statusText);
  }

  if (res.status === 204 || res.headers.get('content-length') === '0') return undefined as T;
  return res.json() as Promise<T>;
}

// ─── Auth ───────────────────────────────────────────────────────────────────

export const auth = {
  register: (email: string, fullName: string) =>
    request<{ message: string; userId?: string; nextStep?: string }>('/auth/register', { method: 'POST', body: JSON.stringify({ email, fullName }) }),
  login: (email: string) =>
    request<{ message: string }>('/auth/login', { method: 'POST', body: JSON.stringify({ email }) }),
  verify: (email: string, code: string) =>
    request<{ accessToken: string; user: User }>('/auth/verify', { method: 'POST', body: JSON.stringify({ email, code }) }),
  me: () => request<{ user: User }>('/auth/me'),
  logout: () => request<void>('/auth/logout', { method: 'POST' }),
  onboarding: (data: OnboardingData) =>
    request<{ message: string; user: User; redirectTo: string }>('/auth/onboarding', { method: 'POST', body: JSON.stringify(data) }),
};

// ─── Agents ─────────────────────────────────────────────────────────────────

export const agents = {
  list: () => request<{ agents: AgentSummary[] }>('/agents'),
  get: (agentType: string) => request<{ agent: AgentStatus }>(`/agents/${agentType}`),
  initialize: (agentType: string) => request<void>(`/agents/${agentType}/initialize`, { method: 'POST' }),
  updateSettings: (agentType: string, settings: AgentSettingsUpdate) =>
    request<void>(`/agents/${agentType}/settings`, { method: 'PATCH', body: JSON.stringify(settings) }),
  run: (agentType: string) => request<void>(`/agents/${agentType}/run`, { method: 'POST' }),
  searchMemory: (agentType: string, query: string) =>
    request<{ results: MemoryResult[] }>(`/agents/${agentType}/memory?q=${encodeURIComponent(query)}`),
  train: (agentType: string, content: string, type: string) =>
    request<void>(`/agents/${agentType}/train`, { method: 'POST', body: JSON.stringify({ content, type }) }),
  logs: (agentType: string, limit = 100, offset = 0) =>
    request<{ logs: ActionLog[]; total: number; limit: number; offset: number }>(
      `/agents/${agentType}/logs?limit=${limit}&offset=${offset}`,
    ),
  logsAll: (limit = 50) =>
    request<{ logs: ActionLog[]; total: number }>(`/agents/logs/all?limit=${limit}`),

  pauseAll: () => request<{ message: string; count: number }>('/agents/pause-all', { method: 'POST' }),
  enableAll: () => request<{ message: string; count: number }>('/agents/enable-all', { method: 'POST' }),
  trainingStatus: () => request<TrainingStatus>('/agents/training/status'),

  // Governance
  trust: (agentType: string) => request<{
    trustScore: number;
    maturityLevel: string;
    totalInteractions: number;
    recentEvents: Array<{ event_type: string; delta: number; trust_after: number; created_at: number }>;
  }>(`/agents/${agentType}/trust`),
  directives: (agentType: string) => request<{
    directives: Array<{ id: string; issuer_agent: string; directive_type: string; content: string; expires_at: number; created_at: number }>;
  }>(`/agents/${agentType}/directives`),

  // Autonomy rules
  autonomyRules: () => request<{ rules: AutonomyRule[] }>('/agents/autonomy-rules'),
  createAutonomyRule: (data: { agentType?: string; actionType: string; ruleType: 'auto_approve' | 'always_queue' }) =>
    request<{ id: string; message: string }>('/agents/autonomy-rules', { method: 'POST', body: JSON.stringify(data) }),
  deleteAutonomyRule: (ruleId: string) =>
    request<{ message: string }>(`/agents/autonomy-rules/${ruleId}`, { method: 'DELETE' }),

  // Graduations
  autonomyGraduations: () => request<{ graduations: AutonomyGraduation[] }>('/agents/autonomy-graduations'),
  acknowledgeGraduation: (agentType: string, graduationId: string, accepted: boolean) =>
    request<{ message: string; controlMode?: string }>(`/agents/${agentType}/graduation/acknowledge`, {
      method: 'POST', body: JSON.stringify({ graduationId, accepted }),
    }),

  // Batch mode
  setAllModes: async (mode: string) => {
    const res = await agents.list();
    await Promise.allSettled(res.agents.map(a => agents.updateSettings(a.agentType, { controlMode: mode })));
    return { message: `All agents set to ${mode}` };
  },

  // Returns an EventSource-compatible URL for streaming chat
  chatUrl: (agentType: string) => `${BASE_URL}/agents/${agentType}/chat`,
  chatHeaders: () => {
    const token = localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  },
};

// ─── Leads ──────────────────────────────────────────────────────────────────

export const leads = {
  list: (params?: LeadListParams) => {
    const qs = new URLSearchParams(params as Record<string, string>).toString();
    return request<{ leads: Lead[]; total: number; page: number; totalPages: number }>(`/leads${qs ? `?${qs}` : ''}`);
  },
  get: (id: string) => request<{ lead: Lead }>(`/leads/${id}`),
  create: (data: Partial<Lead>) => request<{ lead: Lead }>('/leads', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<Lead>) =>
    request<{ lead: Lead }>(`/leads/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) => request<void>(`/leads/${id}`, { method: 'DELETE' }),
  pipeline: () => request<{ pipeline: Array<{ status: string; count: number; value: number }> }>('/leads/pipeline'),
  stats: () => request<LeadStats>('/leads/stats'),
  contact: (id: string, channel: string, message: string) =>
    request<void>(`/leads/${id}/contact`, { method: 'POST', body: JSON.stringify({ channel, message }) }),
  addNote: (id: string, note: string) =>
    request<void>(`/leads/${id}/note`, { method: 'POST', body: JSON.stringify({ content: note }) }),
  bulkImport: (leadsData: Partial<Lead>[]) =>
    request<{ imported: number; failed: number }>('/leads/import', { method: 'POST', body: JSON.stringify({ leads: leadsData }) }),
  export: () => `${BASE_URL}/leads/export`,
};

// ─── Dashboard ──────────────────────────────────────────────────────────────

export const dashboard = {
  summary: () => request<DashboardSummary>('/dashboard/summary'),
  activity: (limit?: number) => request<{ activity: ActivityItem[] }>(`/dashboard/activity-feed${limit ? `?limit=${limit}` : ''}`),
  metrics: (period?: string) => request<{
    metrics: MetricPoint[];
    agentStats: Array<{ agent_type: string; total: number; successes: number; failures: number }>;
    leadsByStatus: Array<{ status: string; count: number; value: number }>;
  }>(`/dashboard/metrics${period ? `?period=${period}` : ''}`),
  briefing: () => request<{ briefing: string }>('/dashboard/briefing'),
  approvals: () => request<{ approvals: Approval[] }>('/dashboard/pending-approvals'),
  approve: (id: string, approved: boolean, note?: string) =>
    request<void>(`/dashboard/approve/${id}`, { method: 'POST', body: JSON.stringify({ approved, note }) }),
  notifications: () => request<{ notifications: Notification[]; unreadCount: number }>('/dashboard/notifications'),
  markNotificationRead: (id: string) => request<void>(`/dashboard/notifications/${id}/read`, { method: 'POST' }),
  markAllNotificationsRead: () => request<void>('/dashboard/notifications/read-all', { method: 'DELETE' }),
  eventsRecent: (limit = 50) => request<{ events: AgentEvent[] }>(`/dashboard/events/recent?limit=${limit}`),
  revenue: (period?: string) => request<{ attribution: RevenueAttribution[]; total: number }>(`/dashboard/revenue${period ? `?period=${period}` : ''}`),
  budget: () => request<{
    creditBalance: number; creditsUsedToday: number; dailyCap: number | null; tier: string;
    tierName: string; monthlyCredits: number;
    isTrial: boolean; trialDaysLeft: number | null; trialEndsAt: number | null;
    isEnterprise: boolean; isFounder: boolean;
    allowance: { total: number; used: number; remaining: number } | null;
    overage: { creditsUsed: number; amountCents: number; capCents: number; ratePerCredit: number };
  }>('/dashboard/budget'),
  campaignProposals: (status = 'pending') => request<{
    proposals: Array<{
      id: string; user_id: string; proposed_by: string; trigger_reason: string;
      title: string; options: string; estimated_credits: number; stagnation_score: number;
      status: string; selected_option: string | null; created_at: number;
    }>;
  }>(`/dashboard/campaign-proposals?status=${status}`),
  approveCampaign: (id: string, selectedOption: 'A' | 'B' | 'C') =>
    request<{ message: string }>(`/dashboard/campaign-proposals/${id}/approve`, { method: 'POST', body: JSON.stringify({ selectedOption }) }),
  rejectCampaign: (id: string) =>
    request<{ message: string }>(`/dashboard/campaign-proposals/${id}/reject`, { method: 'POST' }),
  usage: (days = 30) => request<{
    daily: Array<{ summary_date: string; sonnet_calls: number; haiku_calls: number; total_input_tokens: number; total_output_tokens: number; estimated_cost_cents: number }>;
    byAgent: Array<{ agent_type: string; model: string; calls: number; input_tokens: number; output_tokens: number }>;
    today: { sonnetCalls: number; haikuCalls: number };
    period: { days: number };
  }>(`/dashboard/usage?days=${days}`),
  /** Base URL for WebSocket connection (replace https with wss, http with ws) */
  wsUrl: () => {
    const base = BASE_URL.replace(/\/api$/, '');
    // Handle relative URLs by using window.location
    const fullBase = base.startsWith('http') ? base : `${window.location.origin}${base}`;
    const wsBase = fullBase.replace(/^https/, 'wss').replace(/^http/, 'ws');
    return `${wsBase}/api/dashboard/ws`;
  },
};

// ─── Billing ────────────────────────────────────────────────────────────────

export const billing = {
  plans: () => request<{
    tiers: Array<{ id: string; name: string; monthlyPriceCents: number; annualPriceCents: number; monthlyCredits: number; overageRateCents: number; overageCapCents: number; fairUseCap: number }>;
    packs: Array<{ id: string; name: string; credits: number; priceCents: number; perCreditCents: number }>;
  }>('/billing/plans'),
  subscription: () => request<{ subscription: Subscription }>('/billing/subscription'),
  credits: () => request<{
    balance: number; trialEndsAt: number | null; trialCreditsGranted: number; tier: string;
    transactions: Array<{ id: string; amount: number; balance_after: number; type: string; description: string; created_at: number }>;
    packs: Array<{ id: string; name: string; credits: number; priceCents: number; perCreditCents: number }>;
    allowance: { total: number; used: number; remaining: number; rollover: number; fairUseCap: number } | null;
    overage: { creditsUsed: number; amountCents: number; capCents: number; ratePerCredit: number };
  }>('/billing/credits'),
  subscribe: (tier: string, annual?: boolean) =>
    request<{ url: string; sessionId?: string }>('/billing/subscribe', { method: 'POST', body: JSON.stringify({ tier, annual }) }),
  buyCredits: (packId: string) =>
    request<{ url: string; sessionId?: string }>('/billing/buy-credits', { method: 'POST', body: JSON.stringify({ packId }) }),
  allowance: () => request<{
    tier: string; monthlyCredits: number;
    allowance: { total: number; used: number; remaining: number; rollover: number; fairUseCap: number } | null;
  }>('/billing/allowance'),
  portal: () => request<{ url: string }>('/billing/portal', { method: 'POST' }),
  invoices: () => request<{ invoices: Invoice[] }>('/billing/invoices'),
  cancel: () => request<{ message: string }>('/billing/cancel', { method: 'POST' }),
};

// ─── Profile ─────────────────────────────────────────────────────────────────

export const profile = {
  get: () =>
    request<{ user: { notificationPrefs: Record<string, boolean>; learningMode?: string }; businessProfile: Record<string, unknown> | null }>('/auth/me'),
  update: (data: { name?: string; notifications?: Record<string, boolean>; learningMode?: string }) =>
    request<{ message: string }>('/auth/me', {
      method: 'PUT',
      body: JSON.stringify({ fullName: data.name, notifications: data.notifications, learningMode: data.learningMode }),
    }),
  deactivate: () => request<{ message: string }>('/auth/deactivate', { method: 'POST' }),
  deleteAccount: () => request<{ message: string }>('/auth/account', { method: 'DELETE' }),
};

// ─── API Keys ────────────────────────────────────────────────────────────────

export interface ApiKeyInfo {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  isActive: boolean;
  lastUsedAt: number | null;
  createdAt: number;
}

export const apiKeys = {
  list: () => request<{ keys: ApiKeyInfo[] }>('/auth/api-keys'),
  create: (name: string) =>
    request<{ apiKey: string; id: string; name: string; prefix: string }>('/auth/api-keys', {
      method: 'POST', body: JSON.stringify({ name }),
    }),
  revoke: (id: string) => request<{ message: string }>(`/auth/api-keys/${id}`, { method: 'DELETE' }),
};

// ─── Social ─────────────────────────────────────────────────────────────────

export const social = {
  posts: (params?: { platform?: string; status?: string }) => {
    const qs = new URLSearchParams(params as Record<string, string>).toString();
    return request<{ posts: SocialPost[] }>(`/social/posts${qs ? `?${qs}` : ''}`);
  },
  generate: (topic: string, platform: string, tone?: string) =>
    request<{ content: string; hashtags: string[] }>('/social/generate', {
      method: 'POST', body: JSON.stringify({ topic, platform, tone }),
    }),
  createPost: (data: { platform: string; content: string; mediaUrls?: string[]; scheduledFor?: number }) =>
    request<{ id: string; message: string }>('/social/posts', {
      method: 'POST', body: JSON.stringify(data),
    }),
  initiateOAuth: (platform: string) =>
    request<{ authUrl: string }>(`/social/oauth/${platform}`, { method: 'POST' }),
  connections: () => request<{ connections: Array<{ provider: string; email: string | null; is_active: number; created_at: number }> }>('/social/connections'),
  publishPost: (id: string) => request<{ postUrl?: string; message: string }>(`/social/posts/${id}/publish`, { method: 'POST' }),
  generateVideo: (data: { topic: string; platform: string; tone?: string; duration?: number; aspectRatio?: '16:9' | '9:16' | '1:1' }) =>
    request<{ videoUrl: string; mediaId: string; duration: number; aspectRatio: string; caption: string; hashtags: string[] }>('/social/generate-video', {
      method: 'POST', body: JSON.stringify(data),
    }),
};

// ─── Voice ───────────────────────────────────────────────────────────────────

export interface VoiceClone {
  id: string;
  name: string;
  provider: string;
  provider_voice_id: string;
  preview_url: string | null;
  status: string;
  created_at: number;
}

export const voice = {
  clones: () => request<{ clones: VoiceClone[] }>('/voice/clones'),
  clone: (formData: FormData) => {
    const token = localStorage.getItem('access_token');
    return fetch(`${BASE_URL}/voice/clone`, {
      method: 'POST',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: formData,
      credentials: 'include',
    }).then(async (res) => {
      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: 'Clone failed' })) as { error?: string };
        throw new ApiError(res.status, data.error ?? 'Clone failed');
      }
      return res.json() as Promise<{ id: string; voiceId: string; name: string; message: string }>;
    });
  },
  deleteClone: (id: string) => request<{ message: string }>(`/voice/clones/${id}`, { method: 'DELETE' }),
  preview: (text: string, voiceCloneId?: string, xaiVoice?: string) =>
    request<{ previewUrl: string }>('/voice/preview', {
      method: 'POST', body: JSON.stringify({ text, voiceCloneId, xaiVoice }),
    }),
  xaiVoices: () => request<{ voices: Array<{ id: string; name: string; description: string; style: string }> }>('/voice/xai-voices'),
};

// ─── Slack ───────────────────────────────────────────────────────────────────

export const slack = {
  status: () => request<{ integration: SlackIntegration | null }>('/slack/status'),
  installUrl: () => `${BASE_URL}/slack/install`,
  updatePreferences: (prefs: {
    notifyApprovals?: boolean;
    notifyLeads?: boolean;
    notifyErrors?: boolean;
    notifyBilling?: boolean;
    approvalChannel?: string;
    alertsChannel?: string;
  }) => request<{ message: string }>('/slack/preferences', { method: 'PATCH', body: JSON.stringify(prefs) }),
  test: () => request<{ message: string }>('/slack/test', { method: 'POST' }),
  disconnect: () => request<{ message: string }>('/slack', { method: 'DELETE' }),
};

// ─── Marketplace ─────────────────────────────────────────────────────────────

export const marketplace = {
  browse: (params?: { category?: string; q?: string; sort?: string }) => {
    const qs = new URLSearchParams(params as Record<string, string>).toString();
    return request<{ listings: MarketplaceListing[]; categories: { category: string; count: number }[]; total: number }>(
      `/marketplace${qs ? `?${qs}` : ''}`,
    );
  },
  stats: () => request<{ totalListings: number; totalInstalls: number; totalCreators: number }>('/marketplace/stats'),
  create: (data: { name: string; description: string; emoji?: string; category?: string; systemPrompt: string; toolsConfig?: string[]; priceCents?: number; billing?: 'one_time' | 'monthly'; tags?: string[] }) =>
    request<{ id: string; status: string; message: string }>('/marketplace', { method: 'POST', body: JSON.stringify(data) }),
  publish: (id: string) => request<{ message: string }>(`/marketplace/${id}/publish`, { method: 'POST' }),
  install: (id: string) =>
    request<{ installId?: string; name?: string; message: string; requiresPayment?: boolean; priceCents?: number }>(
      `/marketplace/${id}/install`, { method: 'POST' },
    ),
  uninstall: (id: string) => request<{ message: string }>(`/marketplace/${id}/install`, { method: 'DELETE' }),
};

// ─── Integrations ─────────────────────────────────────────────────────────────

export const integrations = {
  list: () =>
    request<{ connected: ConnectedIntegration[]; catalog: IntegrationMeta[]; connectedCount: number }>('/integrations'),
  catalog: () =>
    request<{ integrations: IntegrationMeta[] }>('/integrations/catalog'),
  get: (service: string) =>
    request<{ integration: ConnectedIntegration; catalog: IntegrationMeta }>(`/integrations/${service}`),
  connect: (service: string, config: Record<string, string>, displayName?: string) =>
    request<{ success: boolean; service: string; displayName: string }>('/integrations', {
      method: 'POST',
      body: JSON.stringify({ service, config, displayName }),
    }),
  update: (service: string, config: Record<string, string>) =>
    request<{ success: boolean }>(`/integrations/${service}`, {
      method: 'PUT',
      body: JSON.stringify({ config }),
    }),
  disconnect: (service: string) =>
    request<{ success: boolean; message: string }>(`/integrations/${service}`, { method: 'DELETE' }),
  test: (service: string) =>
    request<{ success: boolean; error?: string; accountName?: string }>(`/integrations/${service}/test`, {
      method: 'POST',
    }),
};

// ─── Coaching ─────────────────────────────────────────────────────────────────

export interface CoachingSession {
  id: string;
  user_id: string;
  agent_type: string;
  period_start: number;
  period_end: number;
  actions_total: number;
  actions_succeeded: number;
  success_rate: number;
  strengths: string;         // JSON string[]
  development_areas: string; // JSON string[]
  coaching_cue: string | null;
  playbook_name: string | null;
  playbook_strategy: string | null;
  skill_ratings: string;     // JSON Record<string, number>
  created_at: number;
}

export interface AgentSkill {
  id: string;
  user_id: string;
  agent_type: string;
  skill_name: string;
  skill_score: number;
  trend: 'improving' | 'declining' | 'stable';
  last_assessed: number;
}

export interface CoachingSummary {
  sessions: CoachingSession[];
  skills: AgentSkill[];
  stats: {
    totalCoached: number;
    avgSuccessRate: number;
    totalSessions: number;
  };
}

export const coaching = {
  summary: () => request<CoachingSummary>('/coaching/summary'),
  session: (agentType: string, limit = 5) =>
    request<{ sessions: CoachingSession[] }>(`/coaching/${agentType}/session?limit=${limit}`),
  skills: (agentType: string) =>
    request<{ skills: AgentSkill[] }>(`/coaching/${agentType}/skills`),
  leaderboard: () =>
    request<{ leaderboard: Array<{ agent_type: string; success_rate: number; actions_total: number; playbook_name: string | null; coaching_cue: string | null; created_at: number }> }>('/coaching/leaderboard'),
};

// ─── Brain ────────────────────────────────────────────────────────────────────

export const brain = {
  summary: () => request<BrainSummary>('/brain/summary'),
  profile: () => request<{ profile: Record<string, unknown>; agentProfiles: Array<{ agentType: string; interactionCount: number }> }>('/brain/profile'),
  learnings: (params?: { type?: string; limit?: number; offset?: number }) => {
    const qs = new URLSearchParams(params as Record<string, string>).toString();
    return request<{ learnings: SharedLearning[]; total: number; limit: number; offset: number }>(
      `/brain/learnings${qs ? `?${qs}` : ''}`,
    );
  },
  signals: (params?: { event_type?: string; limit?: number; offset?: number }) => {
    const qs = new URLSearchParams(params as Record<string, string>).toString();
    return request<{ signals: TeamSignal[]; total: number; limit: number; offset: number }>(
      `/brain/signals${qs ? `?${qs}` : ''}`,
    );
  },
  performance: (days?: number) =>
    request<BrainPerformance>(`/brain/performance${days ? `?days=${days}` : ''}`),
  training: (limit?: number) =>
    request<{ training: TrainingEntry[]; total: number }>(
      `/brain/training${limit ? `?limit=${limit}` : ''}`,
    ),
};

// ─── Types ──────────────────────────────────────────────────────────────────

export interface User {
  id: string;
  email: string;
  name: string;
  tier: string;
  onboardingCompleted: boolean;
  createdAt: string;
}

export interface OnboardingData {
  businessName: string;
  industry: string;
  website?: string;
  description?: string;
  timezone: string;
}

export interface AgentSummary {
  agentType: string;
  label: string;
  emoji: string;
  status: 'active' | 'idle' | 'paused' | 'uninitialized';
  isEnabled: boolean;
  controlMode: string;
  totalActions: number;
  successRate: number;
  lastActive: number | null;
  tier: string;
  trustScore?: number;
  maturityLevel?: string;
}

export interface AutonomyRule {
  id: string;
  agent_type: string | null;
  action_type: string;
  rule_type: 'auto_approve' | 'always_queue';
  conditions: string | null;
  apply_count: number;
  created_at: number;
  updated_at: number;
}

export interface AutonomyGraduation {
  id: string;
  agent_type: string;
  previous_mode: string;
  new_mode: string;
  reason: string;
  trust_score: number;
  approval_rate: number;
  user_acknowledged: number;
  created_at: number;
}

export interface AgentStatus extends AgentSummary {
  memory: MemoryResult[];
  recentActions: ActionLog[];
}

export interface AgentSettingsUpdate {
  isEnabled?: boolean;
  controlMode?: string;
  customInstructions?: string;
}

export interface MemoryResult {
  id: string;
  content: string;
  score: number;
  metadata: Record<string, unknown>;
}

export interface ActionLog {
  id: string;
  agent_type: string;
  action_type: string;
  success: number;
  result: string | null;
  error_message: string | null;
  tokens_used: number | null;
  duration_ms: number | null;
  tool_calls: string | null;
  created_at: number;
  // legacy fields kept for compatibility
  actionType?: string;
  createdAt?: string;
}

export interface Lead {
  id: string;
  name: string;
  email: string;
  phone?: string;
  company?: string;
  jobTitle?: string;
  status: string;
  source: string;
  score: number;
  value: number;
  notes?: string;
  tags?: string[];
  assignedAgent?: string;
  lastContact?: string;
  createdAt: string;
  updatedAt: string;
}

export interface LeadListParams {
  page?: string;
  limit?: string;
  status?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: string;
}

export interface LeadStats {
  total: number;
  byStatus: Record<string, number>;
  totalValue: number;
  averageScore: number;
  conversionRate: number;
}

export interface DashboardSummary {
  activeAgents: number;
  totalAgents: number;
  leadsToday: number;
  revenueThisMonth: number;
  actionsToday: number;
  pendingApprovals: number;
  successRate: number;
  tier: string;
}

export interface ActivityItem {
  id: string;
  agentType: string;
  actionType: string;
  success: boolean;
  details: Record<string, unknown>;
  leadName?: string;
  createdAt: string;
}

export interface MetricPoint {
  date: string;
  actions: number;
  leads: number;
  revenue: number;
}

export interface Approval {
  id: string;
  agentType: string;
  actionType: string;
  description: string;
  data: Record<string, unknown>;
  status: string;
  createdAt: string;
}

export interface AgentEvent {
  id?: string;
  agent: string;
  event: string;
  eventType?: string;
  data: Record<string, unknown>;
  timestamp: number;
}

export interface RevenueAttribution {
  agentType: string;
  totalRevenue: number;
  eventCount: number;
  events: Array<{ eventType: string; amount: number; count: number }>;
}

export interface Plan {
  tier: string;
  name: string;
  price: number;
  annualPrice: number;
  description: string;
  agents: number;
  features: string[];
  stripePriceId: string;
  stripeAnnualPriceId: string;
  popular?: boolean;
}

export interface Subscription {
  tier: string;
  status: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
}

export interface Invoice {
  id: string;
  amount: number;
  status: string;
  date: string;
  url?: string;
}

export interface SocialPost {
  id: string;
  platform: string;
  content: string;
  status: string;
  scheduledAt?: string;
  publishedAt?: string;
  engagementLikes?: number;
  engagementShares?: number;
}

export interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  read: boolean;
  createdAt: string;
}

export interface MarketplaceListing {
  id: string;
  creator_user_id: string;
  creator_name?: string;
  name: string;
  description: string;
  emoji: string;
  category: string;
  system_prompt?: string;
  tools_config?: string | string[];
  price_cents: number;
  billing: 'one_time' | 'monthly';
  installs: number;
  rating_avg: number;
  rating_count: number;
  tags?: string | string[];
  status: 'draft' | 'published' | 'suspended';
  is_installed?: boolean;
  is_owner?: boolean;
  published_at?: number;
  created_at: number;
  updated_at: number;
}

export interface SlackIntegration {
  team_name: string;
  bot_user_id: string;
  notify_approvals: number;
  notify_leads: number;
  notify_errors: number;
  notify_billing: number;
  approval_channel: string | null;
  alerts_channel: string | null;
  is_active: number;
  installed_at: number;
}

// ─── Integration Types ───────────────────────────────────────────────────────

export type IntegrationCategory =
  | 'crm' | 'accounting' | 'email_marketing' | 'transactional_email'
  | 'data' | 'support' | 'scheduling' | 'prospecting' | 'communication'
  | 'project' | 'ecommerce' | 'analytics' | 'payments' | 'documents'
  | 'storage' | 'video' | 'social' | 'automation' | 'hr' | 'dev';

export interface IntegrationField {
  key: string;
  label: string;
  type: 'text' | 'password' | 'url';
  placeholder?: string;
  required: boolean;
  hint?: string;
}

export interface IntegrationMeta {
  id: string;
  name: string;
  tagline: string;
  category: IntegrationCategory;
  authType: 'oauth' | 'apikey' | 'basic' | 'webhook' | 'custom';
  fields?: IntegrationField[];
  icon: string;
  color: string;
  popular?: boolean;
  enterprise?: boolean;
  capabilities: string[];
  docsUrl?: string;
  website?: string;
}

export interface ConnectedIntegration {
  service: string;
  display_name: string;
  status: 'active' | 'error' | 'expired';
  connected_at: number;
  last_used: number | null;
  metadata: Record<string, string>;
}

export interface SharedLearning {
  id: string;
  source_agent: string;
  learning_type: 'business_insight' | 'customer_pattern' | 'market_intel' | 'strategy' | 'process';
  content: string;
  confidence: number;
  applies_to: string[];
  applied_count: number;
  created_at: number;
}

export interface TeamSignal {
  id: string;
  event_type: string;
  from_agent: string;
  routed_to: string[];
  payload: Record<string, unknown>;
  priority: 'high' | 'normal';
  created_at: number;
}

export interface BrainSummary {
  totalLearnings: number;
  signalsThisWeek: number;
  latestLearning: { content: string; source_agent: string; created_at: number } | null;
  mostActiveAgent: { agent_type: string; actions: number } | null;
}

export interface AgentPerfStat {
  agent_type: string;
  total_actions: number;
  successes: number;
  success_rate: number;
  avg_duration_ms: number;
  total_tokens: number;
  last_active: number;
}

export interface BrainPerformance {
  agentStats: AgentPerfStat[];
  dailyActivity: Array<{ day: string; actions: number; successes: number }>;
  topTools: Array<{ action_type: string; count: number; successes: number }>;
  period: { days: number; since: number };
}

export interface TrainingEntry {
  type: string;
  content: string;
  agent_type: string | null;
  created_at: number;
}

export interface TrainingStatus {
  hasTraining: boolean;
  types: string[];
  count: number;
}
